import React, { useEffect, useState } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import { CheckmarkFilled, ChevronDown } from '@bloomberg/media-ui-icons';

import { REGIONAL_DATA } from '../constants/constants';
import { REGIONS, THEMES } from '../types';
import setExpPrefCookie from '../utils/expPref';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './RegionPickerMobile.module.css';

export type RegionPickerProps = GlobalProps & {
  region: REGIONS;
  theme?: THEMES;
  trackingFunction: (action: string, eventLabel: string) => void;
};

const ChevronArrow = ({
  isRegionPickerOpen,
  className,
}: {
  className?: string;
  isRegionPickerOpen: boolean;
}) => {
  const cx = classes([
    className,
    {
      [styles.chevronDown]: isRegionPickerOpen,
    },
  ]);

  return (
    <div tabIndex={0} data-component="chevron" className={cx}>
      <ChevronDown />
    </div>
  );
};

export const RegionPickerMobile = React.forwardRef<
  HTMLDivElement,
  RegionPickerProps
>(function RegionPickerComponent(props: RegionPickerProps, ref) {
  const { region = REGIONS.US, theme = THEMES.LIGHT, trackingFunction } = props;

  const [isRegionPickerOpen, setIsRegionPickerOpen] = React.useState(false);
  const [selectedRegion, setSelectedRegion] = useState<REGIONS>(
    Object.values(REGIONS).includes(region) ? region : REGIONS.US
  );
  const translateMessage = useTranslateMessage();

  useEffect(() => {
    if (Object.values(REGIONS).includes(region)) {
      setSelectedRegion(region);
    }
  }, [region]);

  const onRegionSelected = (value: string) => {
    setSelectedRegion(value as REGIONS);
    setIsRegionPickerOpen(false);
    trackingFunction('click', `edition-${value}`);
    setExpPrefCookie(value);

    window.location.href = REGIONAL_DATA[value as REGIONS].url;
  };

  const onRegionClicked = () => {
    setIsRegionPickerOpen(!isRegionPickerOpen);
  };

  const componentName = useSlug('region-picker');

  const cx = classes([
    styles.regionPickerContainer,
    {
      [styles.light]: theme === THEMES.LIGHT,
      [styles.dark]: theme === THEMES.DARK,
      [styles.blue]: theme === THEMES.BLUE,
    },
  ]);

  const { value, text, switchEditionPrompt } = REGIONAL_DATA[selectedRegion];

  return (
    <div className={cx} ref={ref} data-component={componentName}>
      <button
        className={styles.region}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_selectRegion',
          defaultMessage: 'Select Region',
        })}
        data-testid="region-button"
        tabIndex={0}
        onClick={onRegionClicked}
      >
        <div
          className={styles.regionText}
          aria-label={translateMessage({
            id: 'media-ui-nav_arialabel_currentRegion',
            defaultMessage: 'Current Region',
          })}
          data-testid="region"
        >
          {switchEditionPrompt || `${text} Edition`}
        </div>

        <ChevronArrow isRegionPickerOpen={isRegionPickerOpen} />
      </button>
      {isRegionPickerOpen && (
        <div
          className={styles.mobileDropdown}
          aria-label={translateMessage({
            id: 'media-ui-nav_arialabel_regionSelector',
            defaultMessage: 'Region Selector',
          })}
          data-testid="dropdown"
        >
          {Object.values(REGIONAL_DATA).map((option) => {
            return (
              <button
                className={styles.option}
                key={option.value}
                tabIndex={0}
                onClick={() => onRegionSelected(option.value)}
              >
                {option.text}
                {option.value === value && (
                  <CheckmarkFilled className={styles.checkMark} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
